import { Modal, ModalClose } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import React, { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import IntlDateTime from "~/src/shared/components/IntlDateTime";

interface IBalanceTransactionsProps {
  transactions: NonNullable<ICompanyAccountBillingInfoQuery["company"]>["balanceTransactions"];
  children: (toggleOpen: () => void) => ReactElement;
}

export default function BalanceTransactions({ transactions, children }: IBalanceTransactionsProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Modal isOpen={isOpen} ariaHideApp={false} onRequestClose={() => setIsOpen(false)}>
        <div
          css={{
            display: "grid",
            gridRowGap: 20,
            width: 480,
            padding: 20,
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <h2
              css={{
                fontWeight: "bold",
                margin: 0,
              }}
            >
              <FormattedMessage defaultMessage="Balance" id="H5+NAX" />
            </h2>

            <ModalClose
              onClick={() => setIsOpen(false)}
              style={{
                position: "relative",
                top: 0,
                right: 0,
                marginLeft: "auto",
                cursor: "pointer",
              }}
            />
          </div>

          <div>
            <table
              css={{
                width: "100%",
                "& th, td": {
                  textAlign: "left",
                },
              }}
            >
              <thead>
                <tr
                  css={{
                    "& th": {
                      color: palette.darkGray,
                    },
                  }}
                >
                  <th>
                    <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
                  </th>
                  <th>
                    <FormattedMessage defaultMessage="Date" id="P7PLVj" />
                  </th>
                  <th>
                    <FormattedMessage defaultMessage="Amount" id="/0TOL5" />
                  </th>
                </tr>
              </thead>

              <tbody>
                {transactions.length === 0 && (
                  <tr>
                    <td css={{ color: palette.darkGray }} colSpan={3}>
                      <FormattedMessage defaultMessage="No entries available." id="PjWgbw" />
                    </td>
                  </tr>
                )}

                {transactions.map((transaction, index) => {
                  if (transaction.type === "applied_to_invoice") {
                    return (
                      <tr key={`${index}/${transaction.type}`}>
                        <td css={{ color: palette.darkGray }}>
                          <FormattedMessage defaultMessage="Applied to Invoice" id="4OCsqZ" />
                        </td>
                        <td css={{ color: palette.darkGray }}>
                          <IntlDateTime value={transaction.createdAt} />
                        </td>
                        <td
                          css={{
                            color: transaction.amount < 0 ? palette.greenSuccess : palette.red,
                          }}
                        >
                          {formatDollarAmount(transaction.amount)}
                        </td>
                      </tr>
                    );
                  }

                  if (transaction.type === "adjustment" && transaction.government) {
                    return (
                      <tr key={`${index}/${transaction.type}`}>
                        <td css={{ color: palette.darkGray }}>{transaction.description}</td>
                        <td css={{ color: palette.darkGray }}>
                          <IntlDateTime value={transaction.createdAt} />
                        </td>
                        <td
                          css={{
                            color: transaction.amount < 0 ? palette.greenSuccess : palette.red,
                          }}
                        >
                          {formatDollarAmount(transaction.amount)}
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={`${index}/${transaction.type}`}>
                      <td css={{ color: palette.darkGray }}>{transaction.description || "N/A"}</td>
                      <td css={{ color: palette.darkGray }}>
                        <IntlDateTime value={transaction.createdAt} />
                      </td>
                      <td
                        css={{
                          color: transaction.amount < 0 ? palette.greenSuccess : palette.red,
                        }}
                      >
                        {formatDollarAmount(transaction.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>

      {children(() => setIsOpen(true))}
    </>
  );
}

function formatDollarAmount(amount: number) {
  if (amount > 0) {
    return `-$${Math.abs(amount / 100).toFixed(2)}`;
  }

  return `$${Math.abs(amount / 100).toFixed(2)}`;
}
